import React from 'react';

export function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function shiftDateIso(iso: string, deltaDays: number): string {
  if (!iso) return todayIso();
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return todayIso();
  d.setDate(d.getDate() + deltaDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatDueBadge(dueDate: string): string {
  if (!dueDate) return '';
  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return '';
  const dayLabel = ['일', '월', '화', '수', '목', '금', '토'][due.getDay()];
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dueOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.round((dueOnly.getTime() - todayOnly.getTime()) / 86400000);
  let rel = '';
  if (diffDays === 0) rel = ' (오늘)';
  else if (diffDays > 0) rel = ` (+${diffDays})`;
  else rel = ` (${diffDays})`;
  return `${dayLabel}${rel}`;
}

export function copyTitle(title: string): string {
  const t = (title || '제목 없음').trim() || '제목 없음';
  return t.includes('(복사)') ? t : `${t} (복사)`;
}

export const IconReset = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

export const IconMinus = () => (
  <svg width="12" height="2" viewBox="0 0 12 2" fill="none" aria-hidden>
    <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const IconPlus = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

type DateFieldProps = {
  label: string;
  value: string;
  onChange: (iso: string) => void;
  /** 라벨 · 날짜입력 · ㅁ/-/+ 를 한 줄로 */
  inline?: boolean;
  /** 라벨 텍스트 숨김(접근성용 label은 유지) */
  hideLabel?: boolean;
};

export const CreateDateField: React.FC<DateFieldProps> = ({
  label,
  value,
  onChange,
  inline = false,
  hideLabel = false,
}) => {
  const controls = (
    <div className="cp-apn-create-inc">
      <button
        type="button"
        className="cp-apn-create-inc-btn"
        aria-label="날짜 초기화"
        onClick={() => onChange(todayIso())}
      >
        <IconReset />
      </button>
      <button
        type="button"
        className="cp-apn-create-inc-btn"
        aria-label="하루 빼기"
        onClick={() => onChange(shiftDateIso(value, -1))}
      >
        <IconMinus />
      </button>
      <button
        type="button"
        className="cp-apn-create-inc-btn"
        aria-label="하루 더하기"
        onClick={() => onChange(shiftDateIso(value, 1))}
      >
        <IconPlus />
      </button>
    </div>
  );

  const dateWrap = (
    <div className="cp-apn-create-date-wrap">
      {value ? <span className="cp-apn-create-weekday">{formatDueBadge(value)}</span> : null}
      <input
        className="cp-apn-create-date-input"
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={hideLabel ? label : undefined}
      />
    </div>
  );

  if (inline) {
    return (
      <div className="cp-apn-create-field cp-apn-create-field--inline">
        {hideLabel ? null : <label className="cp-apn-create-label">{label}</label>}
        {dateWrap}
        {controls}
      </div>
    );
  }

  return (
    <div className="cp-apn-create-field">
      <div className="cp-apn-create-label-row">
        {hideLabel ? null : <label className="cp-apn-create-label">{label}</label>}
        {controls}
      </div>
      {dateWrap}
    </div>
  );
};

type CoinFieldProps = {
  value: string;
  onChange: (v: string) => void;
  getNum: () => number;
};

export const CreateRewardCoinField: React.FC<CoinFieldProps> = ({ value, onChange, getNum }) => (
  <div className="cp-apn-create-reward-body">
    <div className="cp-apn-create-field">
      <div className="cp-apn-create-label-row">
        <label className="cp-apn-create-label">획득 코인</label>
        <div className="cp-apn-create-inc">
          <button
            type="button"
            className="cp-apn-create-inc-btn"
            aria-label="코인 초기화"
            onClick={() => onChange('0')}
          >
            <IconReset />
          </button>
          <button
            type="button"
            className="cp-apn-create-inc-btn"
            aria-label="코인 빼기"
            onClick={() => onChange(String(Math.max(0, getNum() - 10)))}
          >
            <IconMinus />
          </button>
          <button
            type="button"
            className="cp-apn-create-inc-btn"
            aria-label="코인 더하기"
            onClick={() => onChange(String(getNum() + 10))}
          >
            <IconPlus />
          </button>
        </div>
      </div>
      <input
        className="cp-apn-create-input"
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          if (value === '') onChange('0');
        }}
      />
    </div>
  </div>
);
